import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MAX_SELECTION,
  getActLabel,
  type ActionKey,
  type ActionSelection,
  type GameState,
  type HistoryEntry,
  type PlayerKey,
  type PhaseTimerStatus,
  type Season,
  type ViewMode,
  type WaterManagement,
} from "../vowGameLogic";
import { formatCountdown } from "../utils/time";
import { WATER_MANAGEMENT_LABELS } from "./waterManagementOptions";
import { BUTTON_PRIMARY, PANEL_CLASS } from "./uiConstants";
import { ACTION_ICONS, TILE_STATUS_ICONS } from "./iconAssets";
import ConfirmActionModal from "./ConfirmActionModal";
import GameTileGrid from "./gameboard/GameTileGrid";
import LegendPanel, { LegendActionsPanel } from "./gameboard/LegendPanel";
import MapViewControls from "./gameboard/MapViewControls";
import ScoreSummaryGrid from "./gameboard/ScoreSummaryGrid";
import { PlayerDashboardCard } from "./PlayerDashboardsSection";

type GameBoardSectionProps = {
  gameState: GameState;
  timerStatus?: PhaseTimerStatus | null;
  canPerformActions: boolean;
  showScoreSummary?: boolean;
  seasonOptions?: Season[];
  actionContext?: {
    action: ActionSelection;
    playerKey: PlayerKey;
    label: string;
  } | null;
  showActionDetails?: boolean;
  onSubmitAction?: (action: ActionSelection, selectedTiles: number[]) => void;
  lastWmChoice?: WaterManagement | null;
  selfRole?: PlayerKey | null;
  lastHistory?: HistoryEntry;
  previousHistory?: HistoryEntry;
};

const DEFAULT_SEASONS: Season[] = ["P", "E", "A", "H"];

const GameBoardSection: React.FC<GameBoardSectionProps> = ({
  gameState,
  timerStatus,
  canPerformActions,
  showScoreSummary = true,
  seasonOptions = DEFAULT_SEASONS,
  actionContext,
  showActionDetails = false,
  onSubmitAction,
  lastWmChoice = null,
  selfRole = null,
  lastHistory,
  previousHistory,
}) => {
  const [selectedTiles, setSelectedTiles] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("habitat");
  const [selectedSeason, setSelectedSeason] = useState<Season>("P");
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  const effectiveWmChoice: WaterManagement | null =
    lastWmChoice ??
    (gameState.history.length > 0
      ? gameState.history[gameState.history.length - 1]?.wmChoice ?? null
      : null);
  const lastWmChoiceLabel =
    effectiveWmChoice !== null ? WATER_MANAGEMENT_LABELS[effectiveWmChoice] : null;

  useEffect(() => {
    setSelectedTiles([]);
    setConfirmOpen(false);
  }, [gameState.phase, gameState.annee, actionContext?.action]);

  useEffect(() => {
    if (viewMode !== "water_level") {
      setSelectedSeason("P");
    }
  }, [viewMode]);

  const selectedOverlayMax = useMemo(() => {
    if (viewMode === "habitat" || viewMode === "water_level") {
      return 1;
    }

    return Math.max(
      1,
      ...gameState.plateau.map((tile) => tile.resources[viewMode] || 0)
    );
  }, [gameState.plateau, viewMode]);

  const alreadyChosenForPlayer = useCallback(
    (playerKey: PlayerKey) => {
      switch (playerKey) {
        case "herder":
          return gameState.players.herder.grazedTiles.length;
        case "reed_harvester":
          return gameState.players.reed_harvester.harvestedTiles.length;
        case "hunter":
          return gameState.players.hunter.huntedTiles.length;
        default:
          return 0;
      }
    },
    [gameState.players]
  );

  const handleTileToggle = useCallback(
    (index: number) => {
      if (!canPerformActions || !actionContext) return;
      if (gameState.phase !== "actions") return;

      setSelectedTiles((prev) => {
        if (prev.includes(index)) {
          return prev.filter((tileIndex) => tileIndex !== index);
        }

        const limit =
          MAX_SELECTION[actionContext.playerKey] ?? Number.POSITIVE_INFINITY;
        const alreadyChosen = alreadyChosenForPlayer(actionContext.playerKey);

        if (Number.isFinite(limit) && prev.length + alreadyChosen >= limit) {
          return prev;
        }

        return [...prev, index];
      });
    },
    [actionContext, alreadyChosenForPlayer, canPerformActions, gameState.phase]
  );

  const handleViewChange = useCallback((view: ViewMode) => {
    setViewMode(view);
  }, []);

  const remainingSelections = useMemo(() => {
    if (!actionContext) return null;
    const limit =
      MAX_SELECTION[actionContext.playerKey] ?? Number.POSITIVE_INFINITY;
    if (!Number.isFinite(limit)) return null;

    const alreadyChosen = alreadyChosenForPlayer(actionContext.playerKey);
    return Math.max(0, limit - alreadyChosen - selectedTiles.length);
  }, [actionContext, alreadyChosenForPlayer, selectedTiles.length]);

  const currentActionIcon = useMemo(() => {
    if (!actionContext) return null;
    const [actionKey] = actionContext.action.split("-") as [ActionKey, string];
    if (actionKey === "install_nest") {
      return TILE_STATUS_ICONS.nest;
    }
    return ACTION_ICONS[actionKey] ?? null;
  }, [actionContext]);

  const handleSeasonChange = useCallback(
    (season: Season) => {
      if (viewMode !== "water_level") return;
      setSelectedSeason(season);
    },
    [viewMode]
  );

  const canSubmitAction =
    Boolean(actionContext) &&
    canPerformActions &&
    gameState.phase === "actions" &&
    selectedTiles.length > 0;

  const selfPlayer = selfRole ? gameState.players[selfRole] : null;

  const handleAttemptSubmit = useCallback(() => {
    if (!canSubmitAction) return;
    setConfirmOpen(true);
  }, [canSubmitAction]);

  const handleConfirmSubmit = useCallback(() => {
    if (!onSubmitAction || !actionContext) return;
    onSubmitAction(actionContext.action, selectedTiles);
    setSelectedTiles([]);
    setConfirmOpen(false);
  }, [actionContext, onSubmitAction, selectedTiles]);

  const handleCancelSubmit = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const timerInfo =
    timerStatus && timerStatus.phase === "actions"
      ? {
          expired: timerStatus.hasExpired,
          remaining: formatCountdown(timerStatus.remainingMs),
        }
      : null;

  const sidebarSections: React.ReactNode[] = [];

  if (showScoreSummary) {
    sidebarSections.push(<ScoreSummaryGrid key="scores" scores={gameState.scores} />);
  }

  if (selfRole && selfPlayer) {
    sidebarSections.push(
      <React.Fragment key="player-dashboard">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Your Dashboard</h3>
          <p className="mt-1 text-xs text-slate-700">
            Monitor your score and key indicators for this role.
          </p>
        </div>
        <PlayerDashboardCard
          playerKey={selfRole}
          player={selfPlayer}
          lastHistory={lastHistory}
          previousHistory={previousHistory}
        />
      </React.Fragment>
    );
  }

  if (showActionDetails && actionContext) {
    sidebarSections.push(
      <div
        key="action-details"
        className="rounded-lg border-2 border-black bg-[#dcd2b5] p-4"
      >
        <div className="flex items-center gap-3">
          {currentActionIcon && (
            <img
              src={currentActionIcon.src}
              alt={currentActionIcon.alt}
              className="h-10 w-10 flex-shrink-0 object-contain drop-shadow-sm"
            />
          )}
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Current Action</h3>
            <p className="text-sm text-slate-800">{actionContext.label}</p>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-700">
          Selected tiles: {selectedTiles.length}
        </p>
        {remainingSelections !== null && (
          <p className="mt-1 text-xs text-slate-700">
            Remaining actions available: {remainingSelections}
          </p>
        )}
        <button
          type="button"
          onClick={handleAttemptSubmit}
          className={`mt-3 w-full ${BUTTON_PRIMARY} ${!canSubmitAction ? "cursor-not-allowed opacity-60 hover:bg-black" : ""}`}
          disabled={!canSubmitAction}
        >
          Submit Action
        </button>
      </div>
    );
  }

  const hasSidebar = sidebarSections.length > 0;

  return (
    <section className={`${PANEL_CLASS} p-6`}>
      <h2 className="text-2xl font-semibold">Game Board</h2>
      <div className="mt-1 text-sm text-slate-700">
        <span id="stage-label">
          {getActLabel(gameState.annee)}
        </span>
      </div>
      {timerInfo && (
        <div
          className={`mt-2 rounded border border-black px-3 py-2 text-sm font-semibold ${
            timerInfo.expired
              ? "bg-[#f6d0ca] text-rose-900"
              : "bg-[#f4f0e0] text-slate-800"
          }`}
        >
          {timerInfo.expired
            ? "Planning timer ended. Waiting for the host to continue."
            : `Planning timer remaining: ${timerInfo.remaining}`}
        </div>
      )}
      {lastWmChoiceLabel && (
        <div className="mt-1 text-xs font-medium text-slate-700">
          Last WM regime:{" "}
          <span className="font-semibold text-slate-900">
            {lastWmChoiceLabel}
          </span>
        </div>
      )}

      <div className="mt-4 flex flex-col items-center gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex w-full min-w-[280px] flex-col gap-4 lg:flex-1">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-6">
            <div className="w-full lg:flex-1">
              <GameTileGrid
                gameState={gameState}
                selectedTiles={selectedTiles}
                onTileClick={handleTileToggle}
                canPerformActions={canPerformActions && Boolean(actionContext)}
                viewMode={viewMode}
                selectedSeason={selectedSeason}
                selectedOverlayMax={selectedOverlayMax}
              />
            </div>
            <div className="flex-shrink-0 h-full">
              <LegendPanel viewMode={viewMode} />
            </div>
          </div>
          <LegendActionsPanel />
          <MapViewControls
            viewMode={viewMode}
            onViewChange={handleViewChange}
            selectedSeason={selectedSeason}
            onSeasonChange={handleSeasonChange}
            seasonOptions={seasonOptions}
          />
        </div>

        {hasSidebar && (
          <aside className="flex w-full max-w-sm flex-col gap-6">
            {sidebarSections.map((section) => section)}
          </aside>
        )}
      </div>

      <ConfirmActionModal
        isOpen={isConfirmOpen}
        actionLabel={actionContext?.label ?? "this action"}
        selectedCount={selectedTiles.length}
        remainingSelections={remainingSelections}
        onCancel={handleCancelSubmit}
        onConfirm={handleConfirmSubmit}
      />

    </section>
  );
};

export default GameBoardSection;
